<template>
    <draggable-modal @close="handleClose" ref="modalRef">
        <template #header>
            Fifty Percent Line
        </template>
        <template #body>
            <div class="flex flex-wrap pb-3">
                <div class="w-1/2 pr-4">
                    <label for="price1">Price 1:</label>
                    <div class="error" v-if="errors.price1">{{ errors.price1 }}</div>
                    <price-input
                        :base-code="baseCode"
                        :symbol="symbol"
                        class="form-control form-input-sm-inline"
                        :class="{'is-invalid': errors.price1}"
                        :disabled="use52w"
                        :default-value="price1"
                        v-model="price1"
                        id="price1">
                    </price-input>
                </div>
                <div class="w-1/2 pr-4">
                    <label for="price2">Price 2:</label>
                    <div class="error" v-if="errors.price2">{{ errors.price2 }}</div>
                    <price-input
                        :base-code="baseCode"
                        :symbol="symbol"
                        class="form-control form-input-sm-inline"
                        :class="{'is-invalid': errors.price2}"
                        :disabled="use52w"
                        :default-value="price2"
                        v-model="price2"
                        id="price2">
                    </price-input>
                </div>
                <div class="w-full my-2">
                    <label for="52w">
                        <input @change="toggle52w" :value="true" v-model="use52w" id="52w" type="checkbox" class="mr-1" />
                        Use 52-Week High/Low Prices
                    </label>
                </div>

                <div class="w-full">
                    <label for="show-prices">
                        Show Prices:
                        <input :value="true" v-model="showPrices" id="show-prices" type="checkbox" class="ml-1" />
                    </label>
                </div>

                <div class="mt-3">
                    <label>Price Lines:</label>
                    <line-component v-model="priceLineAttributes" :default-value="priceLine"></line-component>
                </div>

                <div class="mt-2">
                    <label>50% Line:</label>
                    <line-component v-model="price50Attributes" :default-value="fiftyLine"></line-component>
                </div>

            </div>
        </template>
        <template #footer>
            <div class="flex justify-center">
                <div class="btn btn-sm btn-yellow inline-flex mx-1" @click="draw">Draw</div>
            </div>
        </template>
    </draggable-modal>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import PriceInput from '../PriceInput';
import LineComponent from './ContextMenu/Line';

const props = defineProps(['defaultValues', 'defaultTraits', 'symbol']);

const emit = defineEmits(['complete', 'close', 'pick-point']);

const price1 = ref('');
const price2 = ref('');
const use52w = ref(false);
const showPrices = ref(props.defaultTraits && props.defaultTraits.showPrices !== undefined ? props.defaultTraits.showPrices : true);
const highLowValues = ref({});
const errors = ref({ price1: '', price2: '' });

const defaultPriceLine = {
    width: props.defaultTraits && props.defaultTraits.line2 && props.defaultTraits.line2.width || 1,
    color: props.defaultTraits && props.defaultTraits.line2 && props.defaultTraits.line.color || '#000',
    dashStyle: props.defaultTraits && props.defaultTraits.line2 && props.defaultTraits.line.dashStyle || 'Dash'
};

const defaultFiftyLine = {
    color: props.defaultTraits && props.defaultTraits.line2 && props.defaultTraits.line2.color || '#5700B8',
    width: props.defaultTraits && props.defaultTraits.line2 && props.defaultTraits.line2.width || 1,
    dashStyle: props.defaultTraits && props.defaultTraits.line2 && props.defaultTraits.line2.dashStyle || 'Solid'
};

const priceLine = ref(defaultPriceLine);
const fiftyLine = ref(defaultFiftyLine);
const priceLineAttributes = ref(defaultPriceLine);
const price50Attributes = ref(defaultFiftyLine);

const baseCode = ref(window.Barchart.getFieldFromInlineTemplate('unitCode'));

const handleClose = () => {
    emit('close');
};

function validate() {
    let valid = true;

    if (price1.value === '') {
        errors.value.price1 = 'Please enter a price';
        valid = false;
    }

    if (price2.value === '') {
        errors.value.price2 = 'Please enter a price';
        valid = false;
    }

    return valid;
}

function draw() {
    if (!validate()) return;

    const localTraits = {
        line: priceLineAttributes.value,
        line2: price50Attributes.value,
        showPrices: showPrices.value
    };

    window.localStorage.setItem('FiftyPercentLine', JSON.stringify(localTraits));

    emit('complete', 'FiftyPercentLine', localTraits, {
        points: [{
            price: price1.value,
            time: Date.now()
        }, {
            price: price2.value,
            time: Date.now()
        }]
    });

    emit('close');
}

function get52wValues() {
    const route = window.route('api.quotes');
    let symbol;
    try {
        symbol = Object.keys(JSON.parse(window.document.getElementById('barchart-www-inline-data').innerHTML))[0];
    } catch (e) {
        console.error(e);
    }

    if (!symbol) return;
    axios.post(route, { symbol, fields: 'symbol,lowPrice1y,highPrice1y', raw: true })
        .then((response) => {
            highLowValues.value = response.data.data[0];
        }).catch(error => {
            console.error(error);
        });
}

function toggle52w() {
    if (use52w.value && highLowValues.value) {
        price1.value = highLowValues.value.raw.lowPrice1y;
        price2.value = highLowValues.value.raw.highPrice1y;
    } else {
        // remove values
    }
}

function pickPoint() {
    emit('pick-point', 'PickPoint');
}

function handleChartClick(eventName, event) {
    if (price1.value === '') {
        // price1.value = eventName.point.price;
        price1.value = event.point.price;

        pickPoint();
    } else if (price2.value === '') {
        // price2.value = eventName.point.price;
        price2.value = event.point.price;
    }
}

function setupClickEvent() {
    const charts = window.Barchart.RealtimeWidgets.Charts;
    charts.PubSub.subscribe(charts.Topics.CH_ANNPOINTPICKED, handleChartClick);
}

onMounted(() => {
    setupClickEvent();
    get52wValues();
});

onBeforeUnmount(() => {
    const charts = window.Barchart.RealtimeWidgets.Charts;
    charts.PubSub.unsubscribe(charts.Topics.CH_ANNPOINTPICKED, handleChartClick);
});
</script>

<style lang="scss">
#modalToolFiftyPercent {
    .modal-align {
        left: 50%;
        margin-left: -13rem;
        @screen xs {
            left: auto;
            margin-left: 0;
        }
    }
    .modal-container {
        width: 24rem;
        @screen xs {
            width: 100%;
        }
    }
}
</style>
